import * as React from 'react';
import { useQueries } from '@tanstack/react-query';
import { Sparkline } from './sparkline';
import {
  getPulsePostureHistory,
  type PulsePostureHistoryResponse,
} from '@/lib/api/pulse-posture-history';

/**
 * Portfolio trend card (T-0107 / C20-130).
 *
 * Mirrors the "Posture mix · 30-day trend" card from
 * `design_handoff_caliber_v2/prototype/screens-portfolio.js`. The line tracks
 * the count of engagements in `red`/`critical` posture across the trailing
 * window, sampled from the T-0131 `pulse-posture-history` per-engagement
 * series.
 *
 * Implementation note: there is no single firm-wide posture-history endpoint
 * today. We fan out one `getPulsePostureHistory` per engagement (range=30d)
 * and bucket by ISO calendar date, counting how many engagements were in
 * critical posture on each day. If no engagements have any history yet, we
 * render an empty-state line so the card never disappears — the trend card
 * is part of the visual contract.
 */

export interface PortfolioTrendCardProps {
  engagementIds: ReadonlyArray<string>;
}

export function PortfolioTrendCard({ engagementIds }: PortfolioTrendCardProps) {
  const histories = useQueries({
    queries: engagementIds.map((id) => ({
      queryKey: ['portfolio', 'posture-history', id] as const,
      queryFn: () => getPulsePostureHistory(id, '30d'),
      staleTime: 60_000,
    })),
  });

  const allLoading = histories.length > 0 && histories.every((q) => q.isLoading);
  const data = histories
    .map((q) => q.data)
    .filter((d): d is PulsePostureHistoryResponse => Boolean(d));

  // Bucket by date — for each date, count how many engagements had `red`
  // posture as their last-known state up to and including that date.
  const series = React.useMemo(() => buildCriticalSeries(data), [data]);
  const latestCount = series.length > 0 ? series[series.length - 1]!.count : 0;

  return (
    <div
      className="flex-1 rounded-card border border-ink-100 bg-paper-0 p-6"
      aria-label="Portfolio posture trend"
    >
      <div className="mb-3 flex items-baseline justify-between gap-3">
        <h2 className="text-h3 font-semibold text-ink-900">Posture mix · 30-day trend</h2>
        <span className="text-meta text-ink-500">
          Across {engagementIds.length} engagement{engagementIds.length === 1 ? '' : 's'}
        </span>
      </div>
      {engagementIds.length === 0 ? (
        <p className="text-body-sm text-ink-500">No engagements to chart.</p>
      ) : allLoading ? (
        <div
          aria-label="Loading trend chart"
          className="h-[160px] w-full animate-pulse rounded bg-ink-100"
        />
      ) : series.length === 0 ? (
        <p className="text-body-sm text-ink-500">
          No posture snapshots yet across these engagements.
        </p>
      ) : (
        <>
          <Sparkline
            values={series.map((p) => p.count)}
            width={540}
            height={160}
            stroke="var(--posture-critical)"
            className="w-full"
            ariaLabel={`Critical engagements over time, current ${latestCount}`}
          />
          <p className="mt-2 text-meta text-ink-500">
            Critical engagements over time — current{' '}
            <span className="font-semibold text-ink-900 tabular-nums">{latestCount}</span>.
          </p>
        </>
      )}
    </div>
  );
}

/**
 * Build a date-bucketed series of "engagements in critical posture" from a
 * set of per-engagement posture-history responses. For each engagement we
 * walk the events forward in time tracking the latest known posture; for
 * each calendar day we count engagements currently in `red`.
 */
function buildCriticalSeries(
  histories: ReadonlyArray<PulsePostureHistoryResponse>,
): ReadonlyArray<{ date: string; count: number }> {
  if (histories.length === 0) return [];

  // Collect all distinct calendar days across all engagements.
  const days = new Set<string>();
  for (const h of histories) {
    for (const it of h.items) {
      days.add(it.captured_at.slice(0, 10));
    }
  }
  if (days.size === 0) return [];

  const sortedDays = Array.from(days).sort();

  // For each engagement, sort items by captured_at and pre-compute "as-of
  // day, what's the latest posture?".
  const series = sortedDays.map((day) => {
    let count = 0;
    for (const h of histories) {
      let latest: string | null = null;
      for (const it of h.items) {
        if (it.captured_at.slice(0, 10) <= day) latest = it.posture;
      }
      if (latest === 'red') count += 1;
    }
    return { date: day, count };
  });

  return series;
}

PortfolioTrendCard.displayName = 'PortfolioTrendCard';
